import {
  brokerAccountRefKey,
  type BrokerAccountRef,
  resolveEntityBrokerAccounts,
} from "../../../utils/brokerAccountRef";
import {
  createWorkspace,
  listWorkspaces,
} from "../../../services/workspacesApi";
import type { StrategyWorkspaceContribution } from "../../../types/workspace";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";

function contributionMatchesAccount(
  contribution: StrategyWorkspaceContribution,
  account: BrokerAccountRef
): boolean {
  const ref =
    resolveEntityBrokerAccounts({
      brokerName: contribution.brokerName,
      brokerAccountCode: contribution.brokerAccountCode,
    })[0] ?? null;
  return Boolean(ref && brokerAccountRefKey(ref) === brokerAccountRefKey(account));
}

/**
 * Resolve a workspace id for a broker account: prefer Strategy contributing
 * canvases, else list that account's workspaces, else create a blank one.
 * Caller must already have set this account as the Tessera active trade account
 * so scoped workspace APIs hit the right broker account.
 */
export async function resolveBrokerAccountWorkspaceId(
  account: BrokerAccountRef,
  contributingWorkspaces: StrategyWorkspaceContribution[] = []
): Promise<string> {
  for (const contribution of contributingWorkspaces) {
    const wsId = contribution.workspaceId?.trim();
    if (!wsId) continue;
    if (contributionMatchesAccount(contribution, account)) return wsId;
  }

  const scope: WorkspaceAccountScope = {
    brokerName: account.brokerName,
    brokerAccountCode: account.brokerAccountCode,
  };
  const { workspaces } = await listWorkspaces(scope);
  const existing = workspaces[0]?.id?.trim();
  if (existing) return existing;

  const created = await createWorkspace(scope, {
    name: account.brokerAccountCode.trim() || "Workspace",
  });
  return created.id;
}
